import React, { useCallback, useMemo, useRef, useState } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { useAccessControl } from '@/components/AccessControl';
import useFormKey from '@/components/ContentEditor/hooks/useFormKey';
import { EditorMode } from '@/components/ContentEditor/types';
import Error404 from '@/components/Error/404';
import Loader from '@/components/Loader';
import { useAppConfig } from '@/hooks/useAppConfig';
import useBeforeUnload from '@/hooks/useBeforeUnload';
import ErrorAccess from '@/modules/corporate/components/ErrorAccess';
import { useEmbeddedExerciseEditor } from '@/modules/corporate/modules/course/components/ExerciseCardContainer/components/ExerciseEditorModal/context';
import {
  ExerciseType,
  ResourceActions,
  ResourceStatusEnum,
  ResourceSubjects,
  ResourceTypeEnum,
} from '@/types/schema';
import { getRandomCardImage } from '@/utils/cardImage';

import { ExerciseEditorDetailFragment } from '../../graphql/fragments/generated/ExerciseEditorDetail';
import Editor from './components/Editor';
import { getInitialValues } from './components/Editor/config';
import { useExerciseEditorDetailQuery } from './graphql/queries/generated/exerciseEditorDetail';
import useExerciseActions from './hooks/useExerciseActions';
import { ExerciseCreatorProps } from './types';

let cleanup: () => void;

function ExerciseCreator({ exerciseId, editorMode, onCreated }: ExerciseCreatorProps) {
  const { t } = useTranslation(['Exercise', 'Scorecard', 'Skills']);
  const isEditMode = editorMode === EditorMode.Edit;
  const formRef = useRef(null);
  const { site } = useAppConfig();
  const [isDirty, setIsDirty] = React.useState(false);
  const [currentExerciseId, setExerciseId] = useState<string | undefined>(() =>
    editorMode === EditorMode.Edit ? exerciseId : undefined,
  );
  const initialLoad = useRef<boolean>(editorMode !== EditorMode.Create);
  const embeddedEditor = useEmbeddedExerciseEditor();

  const resolvedExerciseId = currentExerciseId || exerciseId;

  const [exerciseData] = useExerciseEditorDetailQuery({
    variables: {
      filters: {
        id: resolvedExerciseId!,
        versionStatus: ResourceStatusEnum.Draft,
      },
    },
    pause: !resolvedExerciseId,
  });

  const isDuplicating = editorMode === EditorMode.Duplicate && !currentExerciseId;

  const resolvedIsDirty = isDirty || isDuplicating;

  const exercise = exerciseData.data?.content as ExerciseEditorDetailFragment;

  const userPermission = (exerciseData.data?.content as ExerciseType)?.userPermissions;

  const canEdit = useAccessControl(userPermission, ResourceActions.Edit, ResourceSubjects.Resource);

  const { formKey, onRefresh } = useFormKey(exercise);

  const onSuccessCb = useCallback(() => {
    setIsDirty(false);
    onRefresh();
    cleanup();
  }, [onRefresh]);

  const { onSubmit, fetching, onSaveDraft, onPublish, onSaveDraftAndPreview } = useExerciseActions({
    exerciseId: currentExerciseId,
    onSuccessCb,
    setExerciseId,
    formRef,
    onCreated,
  });

  const defaultCover = useMemo(() => getRandomCardImage(ResourceTypeEnum.Exercise), []);

  const initialValues = useMemo(
    () => getInitialValues({ exercise, site, defaultCover, isDuplicating, t }),
    [defaultCover, site, exercise, isDuplicating, t],
  );

  cleanup = useBeforeUnload(resolvedIsDirty);

  if (exerciseData.fetching && initialLoad.current) {
    return <Loader />;
  }

  if (isEditMode && !canEdit) {
    return <Error404 />;
  }

  if (exercise?.sharedByWorkspace && editorMode !== EditorMode.Duplicate) {
    return <ErrorAccess resource={t('Common:ResourceTypeSM.Exercise')} />;
  }

  return (
    <Editor
      data={{ exercise, initialValues }}
      formRef={formRef}
      methods={{
        onPublish,
        onRevertSuccess: onRefresh,
        onSaveDraft,
        onSaveDraftAndPreview: embeddedEditor ? undefined : onSaveDraftAndPreview,
        onSubmit,
        setIsDirty,
      }}
      state={{
        isDirty: resolvedIsDirty,
        disabled: fetching,
        formKey,
        isDuplicating,
        isEditMode,
      }}
    />
  );
}

export default ExerciseCreator;
